import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Modal, Select, Space, Tooltip, Empty, Badge, Popover } from 'antd';
import { RefreshCw, Link, CreditCard, TagIcon, Loader2, Copy } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  fetchUserStaticIPsAction,
  assignStaticIPAction,
  unassignStaticIPAction,
  fetchPackages
} from "../../../redux/actions/packageAction";
import { getAllBrokersList } from "../../../redux/actions/brokerAction";
import moment from "moment";
import { useToast } from "components/common/custom-toast/Toast";
import LoderCom from "../../common/Loder";
import BrokerService from "../../../services/broker.service";

const { Option } = Select;

const StaticIPManagement = ({ refreshBrokers, setRefreshBrokers }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedIp, setSelectedIp] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const toast = useToast();

  const fetchIPs = async () => {
    setLoading(true);
    const data = await dispatch(fetchUserStaticIPsAction());
    if (data && data.ips) {
      setIps(data.ips);
    }
    setLoading(false);
  };

  const fetchBrokers = async () => {
    const data = await dispatch(getAllBrokersList());
    if (data && data.brokers) {
      setBrokers(data.brokers);
    }
  };

  useEffect(() => {
    fetchIPs();
    fetchBrokers();
    setRefreshBrokers(false);
  }, [refreshBrokers]);

  useEffect(() => {
    const needsPolling = ips.some(ip => ip.status === 'provisioning' || ip.status === 'terminating');

    let interval;
    if (needsPolling) {
      interval = setInterval(() => {
        fetchIPs();
      }, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [ips]);

  const getStatusTag = (status) => {
    const statusMap = {
      'provisioning': { text: "Provisioning", color: "blue", icon: <Loader2 size={12} className="spin me-2" /> },
      'active': { text: "Active", color: "success" },
      'terminating': { text: "Terminating", color: "warning", icon: <Loader2 size={12} className="spin me-2" /> },
      'terminated': { text: "Terminated", color: "error" },
      'failed': { text: "Failed", color: "error" },
    };
    const { text, color, icon } = statusMap[status] || { text: status?.toUpperCase() || "UNKNOWN", color: "default" };
    return (
      <Tag color={color} className="d-inline-flex align-items-center">
        {icon}
        {text}
      </Tag>
    );
  };

  const handleAssign = async () => {
    if (!selectedIp || !selectedBroker) {
      toast.error("Please select a broker");
      return;
    }

    setAssigning(true);
    const data = await dispatch(assignStaticIPAction({
      userIpId: selectedIp.id,
      brokerId: selectedBroker,
    }));
    if (data) {
      toast.success("IP assigned successfully");
      setIsAssignModalOpen(false);
      fetchIPs();
      fetchBrokers();
    }
    setAssigning(false);
  };

  const handleUnassign = async (brokerId) => {
    const data = await dispatch(unassignStaticIPAction({ brokerId }));
    if (data) {
      toast.success("IP unassigned successfully");
      try {
        await BrokerService.enableBroker(brokerId, { "quickTrade": false });
      } catch (err) {
        console.error("Failed to disable quickTrade on IP unassign", err);
      }
      fetchIPs();
      fetchBrokers();
    }
  };

  const handleRenew = async (ip) => {
    setLoading(true);
    const res = await dispatch(fetchPackages({ product_type: "sip" }));
    const pkg = res?.packages?.find(p => p.planType === "Static IP");

    if (pkg) {
      navigate("/payment", {
        state: {
          selectedPlan: {
            ...pkg,
            planType: "Static IP Renewal",
            userIpId: ip.id,
            planId: ip.planId,
            planName: "Static IP Renewal",
          }
        }
      });
    } else {
      toast.error("Static IP plan information not found.");
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("IP Address copied to clipboard");
  };

  const columns = [
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      align: 'left',
      render: (text, record) => {
        const isIPActive = record.status === 'active' && text;
        let displayIP = 'Provisioning...';

        if (text && (record.status === 'active' || record.status === 'terminating')) {
          displayIP = text;
        } else if (record.status === 'terminated') {
          displayIP = 'Terminated';
        } else if (record.status === 'failed') {
          displayIP = 'Failed';
        } else if (record.status === 'terminating') {
          displayIP = 'Terminating...';
        }

        return (
          <Space>
            <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{displayIP}</span>
            {isIPActive && (
              <Tooltip title="Copy IP">
                <Button
                  type="text"
                  size="small"
                  icon={<Copy size={12} className="text-muted" />}
                  onClick={() => copyToClipboard(text)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Assigned Brokers',
      dataIndex: 'brokers',
      key: 'brokers',
      align: 'left',
      render: (assignedBrokers) => {
        if (!assignedBrokers || assignedBrokers.length === 0) {
          return <span style={{ color: '#999', fontSize: '13px' }}>None</span>;
        }

        const firstBroker = assignedBrokers[0];
        const firstBrokerInfo = brokers.find(allB => allB.id === firstBroker.brokerId);

        const renderTagContent = (b) => {
          const brokerInfo = brokers.find(allB => allB.id === b.brokerId);
          return (
            <Tag
              color="purple"
              key={b.brokerId}
              style={{ margin: '2px', borderRadius: '4px' }}
            >
              <span style={{ fontWeight: 500 }}>{b.brokerName.toUpperCase()}</span>
              {brokerInfo && <span style={{ fontSize: '11px', opacity: 0.7, marginLeft: '4px' }}>({brokerInfo.userName})</span>}
            </Tag>
          );
        };

        const popoverContent = (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px', padding: '4px' }}>
            {assignedBrokers.map(b => renderTagContent(b))}
          </div>
        );

        const displayText = assignedBrokers.length > 1
          ? `${firstBroker.brokerName.toUpperCase()} + ${assignedBrokers.length - 1} more`
          : `${firstBroker.brokerName.toUpperCase()} ${firstBrokerInfo ? '(' + firstBrokerInfo.userName + ')' : ''}`;

        return (
          <Popover content={popoverContent} title="All Assigned Brokers" trigger="hover">
            <Tag color="purple" style={{ cursor: 'pointer', margin: 0 }}>
              {displayText}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      align: 'center',
      render: (date) => moment(date).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Manage IP Assignments">
            <Button
              size="small"
              icon={<Link size={14} />}
              disabled={record.status !== 'active'}
              onClick={() => {
                setSelectedIp(record);
                setSelectedBroker(null);
                setIsAssignModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Renew Plan">
            <Button
              size="small"
              icon={<TagIcon size={14} />}
              disabled={record.status !== 'active'}
              onClick={() => handleRenew(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="static-ip-management">
      <div className="info-note mb-3 p-3 rounded">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <Tag color="orange" style={{ fontWeight: 'bold' }}>Dhan Exclusive</Tag>
            <div className="ms-2" style={{ fontSize: '13px' }}>
              Dedicated IP for Dhan: <strong style={{ fontFamily: 'monospace' }}>103.67.236.182</strong>
              <Tooltip title="Copy IP">
                <Button
                  type="text"
                  size="small"
                  className="p-0 ms-1"
                  icon={<Copy size={12} className="text-muted" />}
                  onClick={() => copyToClipboard("103.67.236.182")}
                  style={{ height: 'auto', display: 'inline-flex' }}
                />
              </Tooltip>
            </div>
          </div>
          <Tooltip title="Use this IP when whitelisting on Dhan portal">
            <span>How to use?</span>
          </Tooltip>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0" style={{ fontWeight: 600 }}>Your Static IPs</h6>
        <Button
          size="small"
          icon={<RefreshCw size={14} />}
          onClick={fetchIPs}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {loading && ips.length === 0 ? (
        <LoderCom />
      ) : (
        <Table
          columns={columns}
          dataSource={ips}
          rowKey="id"
          pagination={false}
          locale={{
            emptyText: <Empty className="mb-4 mt-4" description="No Static IPs found" />
          }}
          scroll={{ x: true }}
          className="static-ip-table"
        />
      )}

      <Modal
        title="Manage IP Assignments"
        open={isAssignModalOpen}
        onOk={selectedBroker ? handleAssign : () => setIsAssignModalOpen(false)}
        onCancel={() => setIsAssignModalOpen(false)}
        confirmLoading={assigning}
        okText={selectedBroker ? "Assign New" : "Done"}
      >
        {(() => {
          const currentIp = ips.find(ip => ip.id === selectedIp?.id) || selectedIp;
          return (
            <>
              <div className="mb-4">
                <h6 className="static-ip-section-title">
                  Currently Assigned Brokers
                </h6>
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {currentIp?.brokers?.length > 0 ? (
                    currentIp.brokers.map(b => {
                      const brokerInfo = brokers.find(allB => allB.id === b.brokerId);
                      return (
                        <Tag
                          color="purple"
                          key={b.brokerId}
                          closable
                          onClose={(e) => {
                            e.preventDefault();
                            handleUnassign(b.brokerId);
                          }}
                          style={{ borderRadius: '4px', padding: '2px 8px' }}
                        >
                          {b.brokerName.toUpperCase()} {brokerInfo ? `(${brokerInfo.userName})` : ''}
                        </Tag>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: '12px', color: '#666' }}>No brokers currently assigned.</span>
                  )}
                </div>
              </div>

              <div>
                <h6 className="static-ip-section-title">
                  Assign New Broker
                </h6>
                <p className="mt-2" style={{ fontSize: '12px', color: '#666' }}>
                  Select a broker to link with IP: <strong style={{ fontFamily: 'monospace' }}>{currentIp?.ipAddress}</strong>
                </p>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select a broker account"
                  value={selectedBroker}
                  onChange={(val) => setSelectedBroker(val)}
                >
                  {brokers
                    .filter(b => !ips.some(ip => ip.brokers?.some(assigned => assigned.brokerId === b.id)))
                    .map(b => (
                      <Option key={b.id} value={b.id}>
                        {b.name.toUpperCase()} ({b.userName})
                      </Option>
                    ))}
                </Select>
                <div className="mt-2 text-muted" style={{ fontSize: '11px' }}>
                  Note: Only accounts without an existing Static IP assignment are visible.
                </div>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default StaticIPManagement;
